/**
 * Страница конверта одной модели: график «просмотров на 1 фана» по отчётным
 * дням (10:00→10:00), выбор дня/диапазона, сводка и таблица дней.
 * Ниже линия — лучше конверт (меньше просмотров на фана).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  CartesianGrid, Line, LineChart, ResponsiveContainer,
  Tooltip, XAxis, YAxis, type TooltipProps,
} from 'recharts';
import { api } from '../api/client.js';
import { formatNum, reelsUrl } from '../lib/format.js';
import { ModalPortal } from '../ui/ModalPortal.js';

interface DayRow { date: string; views: number; fans: number; clicks: number | null; covered: boolean }

function fmtDay(d: string): string {
  const dt = new Date(d);
  const next = new Date(dt);
  next.setDate(next.getDate() + 1);
  const f = (x: Date) => x.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
  return `${f(dt)}–${f(next)}`;
}

function Convert({ views, fans }: { views: number; fans: number }) {
  if (fans <= 0) return <span className="muted">нет фанов</span>;
  if (views <= 0) return <span className="muted">нет просмотров</span>;
  return <>1 фан / <b>{formatNum(Math.round(views / fans))}</b> просм.</>;
}

function ConvertTooltip({ active, payload, label }: TooltipProps<number, string>) {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0]?.payload as DayRow & { convert: number | null };
  return (
    <div className="tooltip-card" style={{ minWidth: 190 }}>
      <div className="tooltip-name">{fmtDay(String(label))} · с 10 до 10 утра</div>
      <div className="tooltip-grid">
        <span>Конверт</span>
        <b>{row.convert != null ? `1 фан / ${formatNum(row.convert)} просм.` : '—'}</b>
        <span>Просмотры</span>
        <b>{formatNum(row.views)}</b>
        <span>Фаны</span>
        <b>{formatNum(row.fans)}</b>
      </div>
    </div>
  );
}

interface DayTop {
  date: string;
  name: string;
  totalViews: number;
  totalFans: number;
  accountsCounted: number;
  accounts: { id: string; username: string; igUrl: string | null; status: string | null; views: number }[];
}

/** Топ-10 акков за отчётный день — по клику на строку в таблице дней. */
function DayTopModal({ modelId, date, onClose }: { modelId: string; date: string; onClose: () => void }) {
  const nav = useNavigate();
  const [data, setData] = useState<DayTop | null>(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api<DayTop>(`/api/analytics/model/${modelId}/day/${date}`).then(setData).catch(() => setErr(true));
  }, [modelId, date]);

  return (
    <ModalPortal>
      <div className="modal-backdrop" onClick={onClose}>
        <div className="modal-panel" style={{ maxWidth: 620, maxHeight: '86vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
          <div className="modal-title">
            {data?.name ?? '…'} · {fmtDay(date)}
          </div>
          {err ? (
            <div className="empty-state">Не удалось загрузить данные дня.</div>
          ) : !data ? (
            <div className="empty-state">Загрузка…</div>
          ) : (
            <>
              <div style={{ fontSize: 12.5, color: 'var(--text-subtle)', marginBottom: 12 }}>
                {formatNum(data.totalViews)} просмотров с {data.accountsCounted} акк. · {formatNum(data.totalFans)} фанов
                {' · '}<Convert views={data.totalViews} fans={data.totalFans} />
              </div>
              {data.accounts.length === 0 ? (
                <div className="empty-state">В этот день просмотров по модели не было.</div>
              ) : (
                <table className="admin-table">
                  <thead>
                    <tr><th style={{ width: 28 }}>#</th><th>Аккаунт</th><th>Просмотры</th><th>Доля</th></tr>
                  </thead>
                  <tbody>
                    {data.accounts.map((a, i) => (
                      <tr key={a.id}>
                        <td style={{ color: 'var(--text-subtle)' }}>{i + 1}</td>
                        <td>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                            <button
                              className="link-btn"
                              style={{ fontWeight: 500 }}
                              title="Открыть страницу аккаунта в срмке"
                              onClick={() => nav(`/account/${a.id}`)}
                            >
                              {a.username}
                            </button>
                            {(
                              <a
                                className="copy-mini"
                                href={reelsUrl(a.igUrl, a.username)!}
                                target="_blank"
                                rel="noreferrer"
                                title="Открыть рилсы в Instagram"
                              >
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                  <path d="M15 3h6v6" />
                                  <path d="M10 14 21 3" />
                                </svg>
                              </a>
                            )}
                            {a.status && <span className={`status-chip status-${a.status.replace(' ', '_')}`} style={{ fontSize: 10.5 }}>{a.status}</span>}
                          </span>
                        </td>
                        <td>{formatNum(a.views)}</td>
                        <td style={{ color: 'var(--text-subtle)' }}>
                          {data.totalViews > 0 ? `${Math.round((a.views / data.totalViews) * 100)}%` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
            <button className="btn-secondary" onClick={onClose}>Закрыть</button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

export function ModelAnalyticsPage() {
  const { id } = useParams();
  const [name, setName] = useState('');
  const [days, setDays] = useState<DayRow[]>([]);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [openDay, setOpenDay] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    const qs = new URLSearchParams();
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    api<{ name: string; days: DayRow[] }>(`/api/analytics/model/${id}?${qs}`)
      .then((r) => { setName(r.name); setDays(r.days); })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id, from, to]);

  // Сводка — только по дням, покрытым фан-отчётами: дни до подключения
  // отчётера (просмотры есть, фанов физически нет) конверт не размазывают.
  const total = useMemo(
    () => days.filter((d) => d.covered).reduce(
      (acc, d) => ({
        views: acc.views + d.views,
        fans: acc.fans + d.fans,
        clicks: d.clicks == null ? acc.clicks : (acc.clicks ?? 0) + d.clicks,
      }),
      { views: 0, fans: 0, clicks: null as number | null },
    ),
    [days],
  );
  // Точки графика: конверт только там, где есть и фаны, и просмотры.
  const chartData = useMemo(
    () => days.map((d) => ({
      ...d,
      convert: d.covered && d.fans > 0 && d.views > 0 ? Math.round(d.views / d.fans) : null,
    })),
    [days],
  );
  const hasConvertPoints = chartData.some((d) => d.convert != null);

  if (notFound) return <div className="empty-state">Модель не найдена.</div>;

  return (
    <div className="page-inner" style={{ maxWidth: 1100 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 16, gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 12.5, marginBottom: 4 }}>
            <Link to="/analytics" style={{ color: 'var(--text-subtle)' }}>← Аналитика</Link>
          </div>
          <h1 style={{ margin: 0, fontSize: 24, letterSpacing: '-0.02em' }}>{name || '…'} — конверт</h1>
          <div style={{ color: 'var(--text-subtle)', fontSize: 13, marginTop: 4 }}>
            просмотров на одного фана по отчётным дням (с 10 до 10 утра) · ниже линия — лучше · клик по дню — топ аккаунтов
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label className="field" style={{ margin: 0 }}>
            <span>С (день)</span>
            <input type="date" className="admin-input" value={from} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label className="field" style={{ margin: 0 }}>
            <span>По (день)</span>
            <input type="date" className="admin-input" value={to} onChange={(e) => setTo(e.target.value)} />
          </label>
          {(from || to) && (
            <button className="btn-secondary" onClick={() => { setFrom(''); setTo(''); }}>Сбросить</button>
          )}
        </div>
      </div>

      <div className="stats-row" style={{ gridTemplateColumns: 'repeat(4, 1fr)', margin: '0 0 16px' }}>
        <div className="stat-card">
          <div className="stat-label">Просмотры за период</div>
          <div className="stat-value">{formatNum(total.views)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Клики за период</div>
          <div className="stat-value">{total.clicks == null ? '—' : formatNum(total.clicks)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Фаны за период</div>
          <div className="stat-value">{formatNum(total.fans)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Конверт за период</div>
          <div className="stat-value stat-accent" style={{ fontSize: 18 }}>
            <Convert views={total.views} fans={total.fans} />
          </div>
        </div>
      </div>

      <div className="card card-large" style={{ marginBottom: 16 }}>
        {loading ? (
          <div className="empty-state">Загрузка…</div>
        ) : !hasConvertPoints ? (
          <div className="empty-state">
            Для графика нужны дни, где есть и просмотры, и фаны — они появятся по мере накопления фан-отчётов.
          </div>
        ) : (
          <div style={{ width: '100%', height: 300 }}>
            <ResponsiveContainer>
              <LineChart data={chartData} margin={{ top: 12, right: 16, bottom: 4, left: 4 }}>
                <CartesianGrid stroke="var(--border)" strokeDasharray="4 4" vertical={false} />
                <XAxis dataKey="date" tickFormatter={fmtDay} stroke="var(--text-muted)" tick={{ fontSize: 11 }} />
                <YAxis
                  tickFormatter={(v: number) => formatNum(v)}
                  stroke="var(--text-muted)"
                  tick={{ fontSize: 11 }}
                  width={58}
                  label={{ value: 'просм. / 1 фан', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: 'var(--text-subtle)' } }}
                />
                <Tooltip content={<ConvertTooltip />} />
                <Line
                  type="monotone"
                  dataKey="convert"
                  name="Конверт"
                  stroke="var(--accent)"
                  strokeWidth={2}
                  dot={{ r: 3.5 }}
                  connectNulls
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {openDay && id && <DayTopModal modelId={id} date={openDay} onClose={() => setOpenDay(null)} />}

      {days.length > 0 && (
        <div className="card">
          <div style={{ overflowX: 'auto' }}>
            <table className="admin-table clickable-rows">
              <thead>
                <tr><th>Отчётный день</th><th>Просмотры</th><th>Клики</th><th>Фаны</th><th>Конверт</th></tr>
              </thead>
              <tbody>
                {[...days].reverse().map((d) => (
                  <tr
                    key={d.date}
                    onClick={() => setOpenDay(d.date)}
                    title="Топ аккаунтов за этот день"
                    style={{ cursor: 'pointer', ...(d.covered ? {} : { opacity: 0.45 }) }}
                  >
                    <td>{fmtDay(d.date)}</td>
                    <td>{formatNum(d.views)}</td>
                    <td>{d.clicks == null ? <span className="muted">—</span> : formatNum(d.clicks)}</td>
                    <td>{formatNum(d.fans)}</td>
                    <td>{d.covered ? <Convert views={d.views} fans={d.fans} /> : <span className="muted">нет фан-отчёта</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
